import { DELIVERY_METHODS } from '../../config/delivery.js';
import { state, patch, subscribe } from '../../state/store.js';
import { escapeHtml } from '../../lib/format.js';
import { mountAutocomplete } from '../../lib/autocomplete.js';

const $root = () => document.getElementById('delivery-root');

const providerCache = new Map();
async function loadProvider(methodId) {
  if (providerCache.has(methodId)) return providerCache.get(methodId);
  const method = DELIVERY_METHODS.find((m) => m.id === methodId);
  if (!method?.provider) return null;
  const mod = await method.provider();
  providerCache.set(methodId, mod);
  return mod;
}

function findSub(methodId, subId) {
  const m = DELIVERY_METHODS.find((x) => x.id === methodId);
  return m?.subOptions.find((s) => s.id === subId);
}

// --- Autocomplete controllers (live across re-renders of dependent inputs) -

let acCity = null;
let acWarehouse = null;
let acStreet = null;

/**
 * Guards against overlapping wiring passes.
 *
 * wireAutocompletes() is async — it awaits the carrier's provider module — so
 * two renders in quick succession (changing carrier, then changing your mind)
 * both resume AFTER the final render and both mount a controller on the same
 * input. disposeAutocompletes() cannot help: when the second pass runs it, the
 * first has not assigned its controller yet. The result is two live popups per
 * field, each keystroke firing two requests, and the dropdowns overwriting each
 * other — which reads to the customer as "search stopped working".
 *
 * Every pass takes a ticket; anything but the newest abandons itself.
 */
let wireGeneration = 0;

function disposeAutocompletes() {
  acCity?.destroy(); acCity = null;
  acWarehouse?.destroy(); acWarehouse = null;
  acStreet?.destroy(); acStreet = null;
}

// Triggers the provider's optional resolveAddress hook. Debounced so we
// don't query on every keystroke of the building field. Each provider
// decides internally which deliveryState fields it needs (UP wants street
// + building; NP only needs the cityRef since it falls back to a branch
// postcode in the same city).
let postcodeTimer = null;
async function maybeResolvePostcode() {
  clearTimeout(postcodeTimer);
  postcodeTimer = setTimeout(async () => {
    const provider = await loadProvider(state.delivery.methodId);
    if (!provider?.resolveAddress) return;
    try {
      const result = await provider.resolveAddress(state.delivery);
      patch('delivery', { postcode: result?.postcode || '' });
    } catch (err) {
      console.warn('resolveAddress failed', err);
      patch('delivery', { postcode: '' });
    }
  }, 350);
}

// --- DOM templates --------------------------------------------------------

/** Inline error slot — text populated imperatively by syncErrorMessages(). */
const errSlot = (key) => `<div class="ac-field-error" data-error-for="${key}"></div>`;

function buildSubInputsHtml(methodId, sub) {
  if (!sub) return '';
  const d = state.delivery;
  // Driven by the sub-option's declared `kind`, not by carrier id: hardcoding
  // 'np-poshtomat' meant Meest's locker tab rendered as "Відділення".
  const isPostomat = sub.kind === 'postomat';
  const isUpBranch = methodId === 'ukrposhta'  && state.delivery.subOptionId === 'up-branch';
  const warehouseLabel = isPostomat ? 'Поштомат' : 'Відділення';
  // Search hint inside the input: NP customers search by branch number /
  // street; UP customers usually search by postcode (or street). Different
  // placeholders make that explicit.
  const warehousePlaceholder = isPostomat
    ? 'Номер або адреса'
    : isUpBranch
      ? 'Поштовий індекс або адреса'
      : 'Номер або адреса';

  // Ukrposhta requires the recipient's patronymic — shown first.
  const patronymicInput = methodId === 'ukrposhta' ? `
    <div class="ac-field">
      <label class="ac-label">По батькові отримувача *</label>
      <input class="input" id="dlv-patronymic-input" type="text" autocomplete="off" placeholder="По батькові" value="${escapeHtml(d.patronymic || '')}" />
      ${errSlot('patronymic')}
    </div>` : '';

  const cityInput = sub.requiresCity ? `
    <div class="ac-field">
      <label class="ac-label">Місто</label>
      <input class="input" id="dlv-city-input" type="text" autocomplete="off" placeholder="Введіть назву міста" value="${escapeHtml(d.cityLabel)}" />
      ${errSlot('city')}
    </div>` : '';

  const warehouseInput = sub.requiresBranch ? `
    <div class="ac-field">
      <label class="ac-label">${warehouseLabel}</label>
      <input class="input" id="dlv-warehouse-input" type="text" autocomplete="off" placeholder="${escapeHtml(warehousePlaceholder)}" value="${escapeHtml(d.warehouseLabel)}" ${d.cityRef ? '' : 'disabled'} />
      ${errSlot('warehouse')}
    </div>` : '';

  // Postcode hint for UP адресна доставка once we've resolved one.
  const postcodeHint = (methodId === 'ukrposhta' && sub.requiresAddress && d.postcode)
    ? `<div class="ac-postcode-hint">Поштовий індекс: <strong>${escapeHtml(d.postcode)}</strong></div>`
    : '';

  const streetInput = sub.requiresAddress ? `
    <div class="ac-field">
      <label class="ac-label">Вулиця</label>
      <input class="input" id="dlv-street-input" type="text" autocomplete="off" placeholder="Введіть назву вулиці" value="${escapeHtml(d.streetLabel)}" ${d.cityRef ? '' : 'disabled'} />
      ${errSlot('street')}
    </div>
    <div class="ac-field-row">
      <div class="ac-field">
        <label class="ac-label">Будинок</label>
        <input class="input" id="dlv-building-input" type="text" placeholder="№" value="${escapeHtml(d.building)}" />
        ${errSlot('building')}
      </div>
      <div class="ac-field">
        <label class="ac-label">Квартира</label>
        <input class="input" id="dlv-apartment-input" type="text" placeholder="(опц.)" value="${escapeHtml(d.apartment)}" />
      </div>
    </div>
    ${postcodeHint}
    ${errSlot('postcode')}` : '';

  return `<div class="np-fields">${patronymicInput}${cityInput}${warehouseInput}${streetInput}</div>`;
}

/**
 * Update each delivery error slot in place from state.ui.errors, and toggle
 * the matching input's red outline. Imperative so we don't tear down live
 * autocompletes / focus state on every form submit attempt.
 */
const ERR_KEY_TO_INPUT = {
  patronymic: 'dlv-patronymic-input',
  city:       'dlv-city-input',
  warehouse:  'dlv-warehouse-input',
  street:     'dlv-street-input',
  building:   'dlv-building-input',
};
function syncErrorMessages() {
  const errs = state.ui?.errors || {};
  const root = document.getElementById('delivery-root');
  if (!root) return;
  root.querySelectorAll('[data-error-for]').forEach((el) => {
    const key = el.dataset.errorFor;
    const msg = errs[key] || '';
    el.textContent = msg;
    el.classList.toggle('is-visible', !!msg);
    const inputId = ERR_KEY_TO_INPUT[key];
    if (inputId) {
      const input = document.getElementById(inputId);
      input?.classList.toggle('has-error', !!msg);
    }
  });
}

async function wireAutocompletes(methodId, sub) {
  const myGeneration = ++wireGeneration;
  disposeAutocompletes();
  if (!sub) return;
  const provider = await loadProvider(methodId);
  // A newer pass started while we were loading the provider module — that pass
  // owns the DOM now. Mounting here would leave an orphaned second controller.
  if (myGeneration !== wireGeneration || !provider) return;

  const cityEl = document.getElementById('dlv-city-input');
  const whEl   = document.getElementById('dlv-warehouse-input');
  const stEl   = document.getElementById('dlv-street-input');
  const bldEl  = document.getElementById('dlv-building-input');
  const aptEl  = document.getElementById('dlv-apartment-input');

  if (cityEl) {
    acCity = mountAutocomplete(cityEl, {
      hint: 'Завантаження…',
      emptyText: 'Місто не знайдено',
      minLength: 0,
      fetch: (q, signal) => provider.searchSettlements(q, signal),
      renderItem: (it) => escapeHtml(it.present || it.name),
      getItemLabel: (it) => it.present || it.name,
      onSelect: (it) => {
        patch('delivery', {
          cityRef: it.cityRef,
          settlementRef: it.ref,
          cityLabel: it.present || it.name,
          regionId: it.regionId || '',
          districtId: it.districtId || '',
          warehouseRef: '', warehouseLabel: '',
          streetRef: '', streetLabel: '',
          postcode: '',
        });
        // For address sub-options the provider may resolve a city-level
        // fallback postcode straight away (NP does, UP waits for street +
        // building). No-op for branch/postomat sub-options.
        if (sub.requiresAddress) maybeResolvePostcode();
      },
    });
  }

  if (whEl && sub.requiresBranch) {
    const kind = sub.kind === 'postomat' ? 'postomat' : 'branch';
    const isUp = methodId === 'ukrposhta';
    acWarehouse = mountAutocomplete(whEl, {
      hint: state.delivery.cityRef ? 'Введіть номер або адресу' : 'Спочатку оберіть місто',
      emptyText: kind === 'postomat' ? 'Поштомат не знайдено' : 'Відділення не знайдено',
      minLength: 0,
      fetch: (q, signal) => {
        if (!state.delivery.cityRef) return [];
        return provider.getWarehouses(state.delivery, kind, q, signal);
      },
      renderItem: (it) => escapeHtml(it.description),
      getItemLabel: (it) => it.description,
      onSelect: (it) => {
        patch('delivery', {
          warehouseRef: it.ref,
          warehouseLabel: it.description,
          // Both UP and NP expose a per-warehouse postcode (UP: postcode/
          // postindex; NP: PostalCodeUA → surfaced as `postcode` by our route).
          postcode: it.postcode || it.postindex || '',
        });
      },
    });
  }

  if (stEl && sub.requiresAddress) {
    acStreet = mountAutocomplete(stEl, {
      hint: state.delivery.cityRef ? 'Введіть назву вулиці' : 'Спочатку оберіть місто',
      emptyText: 'Вулицю не знайдено',
      minLength: 2,
      fetch: (q, signal) => {
        if (!state.delivery.cityRef) return [];
        return provider.searchStreets(state.delivery, q, signal);
      },
      renderItem: (it) => escapeHtml(it.present || it.name),
      getItemLabel: (it) => it.present || it.name,
      onSelect: (it) => {
        patch('delivery', { streetRef: it.ref, streetLabel: it.present || it.name, postcode: '' });
        maybeResolvePostcode();
      },
    });
  }

  bldEl?.addEventListener('input', () => {
    patch('delivery', { building: bldEl.value });
    maybeResolvePostcode();
  });
  aptEl?.addEventListener('input', () => patch('delivery', { apartment: aptEl.value }));

  const patEl = document.getElementById('dlv-patronymic-input');
  patEl?.addEventListener('input', () => patch('delivery', { patronymic: patEl.value }));
}

/**
 * After a city pick or sub-option change, dependent inputs need their
 * disabled state and value flipped in place — re-rendering the whole list
 * would destroy whatever input the user is typing in.
 */
function syncDependentInputs() {
  const d = state.delivery;
  const whEl = document.getElementById('dlv-warehouse-input');
  const stEl = document.getElementById('dlv-street-input');
  if (whEl) {
    whEl.disabled = !d.cityRef;
    if (document.activeElement !== whEl) whEl.value = d.warehouseLabel;
  }
  if (stEl) {
    stEl.disabled = !d.cityRef;
    if (document.activeElement !== stEl) stEl.value = d.streetLabel;
  }

  // Show or update the postcode hint imperatively so we don't tear down the
  // input the user might be focused on (building).
  const fieldsRoot = document.querySelector('.np-fields');
  if (fieldsRoot) {
    const existing = fieldsRoot.querySelector('.ac-postcode-hint');
    if (d.postcode && state.delivery.methodId === 'ukrposhta') {
      const html = `Поштовий індекс: <strong>${escapeHtml(d.postcode)}</strong>`;
      if (existing) existing.innerHTML = html;
      else {
        const div = document.createElement('div');
        div.className = 'ac-postcode-hint';
        div.innerHTML = html;
        fieldsRoot.appendChild(div);
      }
    } else if (existing) {
      existing.remove();
    }
  }
}

// --- Top-level render -----------------------------------------------------

function renderMethodList() {
  const root = $root();
  if (!root) return;

  root.innerHTML = DELIVERY_METHODS.map((m) => {
    const isSelected = state.delivery.methodId === m.id;
    const selectedSub = isSelected ? findSub(m.id, state.delivery.subOptionId) ?? m.subOptions[0] : null;

    const subOptionsHtml = m.subOptions.map((s) => {
      const isSelected = state.delivery.subOptionId === s.id && !s.disabled;
      const isDisabled = !!s.disabled;
      return `
        <label class="sub-opt${isDisabled ? ' is-disabled' : ''}"
               data-method="${m.id}" data-sub="${s.id}" data-disabled="${isDisabled ? '1' : '0'}"
               ${isDisabled ? 'aria-disabled="true"' : ''}>
          <input type="radio" name="dm-${m.id}-sub" ${isSelected ? 'checked' : ''} ${isDisabled ? 'disabled' : ''} />
          <span class="radio ${isSelected ? 'is-on' : ''}"></span>
          <span class="sub-opt-label">${escapeHtml(s.label)}</span>
          ${s.notice ? `<span class="sub-opt-notice">${escapeHtml(s.notice)}</span>` : ''}
        </label>
      `;
    }).join('');

    const subInputsHtml = isSelected ? buildSubInputsHtml(m.id, selectedSub) : '';

    return `
      <div class="method${isSelected ? ' is-selected' : ''}" data-id="${m.id}">
        <div class="method-head">
          <span class="radio"></span>
          <div class="method-logo">
            ${m.logoSrc
              ? `<img class="logo-img" src="${escapeHtml(m.logoSrc)}" alt="${escapeHtml(m.name)}" loading="lazy" />`
              : `<span class="logo-chip" style="background:${m.logoBg};color:${m.logoColor || '#fff'}">${escapeHtml(m.logoText)}</span>`}
            ${m.logoSrc && m.logoHasName
              ? ''
              : `<span class="method-name">${escapeHtml(m.name)}</span>`}
          </div>
          <span class="method-arrow" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </span>
        </div>
        <div class="method-body">
          <div class="sub-options">${subOptionsHtml}</div>
          ${subInputsHtml}
        </div>
      </div>
    `;
  }).join('');

  root.querySelectorAll('.method-head').forEach((head) => {
    head.addEventListener('click', () => {
      const id = head.parentElement.dataset.id;
      if (state.delivery.methodId === id) return;
      const m = DELIVERY_METHODS.find((x) => x.id === id);
      patch('delivery', {
        methodId: id,
        subOptionId: m?.subOptions?.[0]?.id ?? null,
        cityRef: '', settlementRef: '', cityLabel: '',
        regionId: '', districtId: '',
        warehouseRef: '', warehouseLabel: '',
        streetRef: '', streetLabel: '',
        building: '', apartment: '',
        postcode: '',
        patronymic: '',
      });
    });
  });

  root.querySelectorAll('.sub-opt').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (el.dataset.disabled === '1') return;
      const methodId = el.dataset.method;
      const subId = el.dataset.sub;
      if (state.delivery.methodId === methodId && state.delivery.subOptionId === subId) return;
      patch('delivery', {
        methodId,
        subOptionId: subId,
        warehouseRef: '', warehouseLabel: '',
        streetRef: '', streetLabel: '',
        building: '', apartment: '',
        postcode: '',
      });
    });
  });

  const selectedSub = findSub(state.delivery.methodId, state.delivery.subOptionId);
  wireAutocompletes(state.delivery.methodId, selectedSub);
}

export function initDelivery() {
  // Safety: if the persisted sub-option for the current method is disabled,
  // fall back to the first enabled one (so we never start on a dead radio).
  const startMethod = DELIVERY_METHODS.find((m) => m.id === state.delivery.methodId);
  const startSub    = startMethod?.subOptions.find((s) => s.id === state.delivery.subOptionId);
  if (startSub?.disabled) {
    const fallback = startMethod.subOptions.find((s) => !s.disabled);
    if (fallback) patch('delivery', { subOptionId: fallback.id });
  }

  let lastMethodId = state.delivery.methodId;
  let lastSubOptionId = state.delivery.subOptionId;

  renderMethodList();

  subscribe(['delivery'], () => {
    if (state.delivery.methodId !== lastMethodId || state.delivery.subOptionId !== lastSubOptionId) {
      lastMethodId = state.delivery.methodId;
      lastSubOptionId = state.delivery.subOptionId;
      renderMethodList();
      syncErrorMessages();
    } else {
      syncDependentInputs();
    }
  });
  // Form-submit attempt sets state.ui.errors — surface them next to each input.
  subscribe(['ui'], () => syncErrorMessages());
}
